// src/features/alerts/components/AlertDetail.tsx
import { Col, Row, Form, Button } from "react-bootstrap";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { convertUnixToFormateDate } from "../../../utils/dateUtils";
import { changeIAValuesAlert } from "../api/updateAlertStatus";
import { SurveyAlertDto } from "../types";
import { useUsersSelect } from "../../../hooks/getUsersByClient";
import { useAlert } from "../../../hooks/useAlert";
import { useCurrentClient } from "../../auth/api/getCurrentClient";
import SentimentIndicator from "../../../components/SentimentIndicatorSelector";
import GenerateAlertSelector from "../../../components/GenerateAlertSelctor";
import AlertDetailWrapper from "./AlertDetailWrapper";
import TopicsSection from "../../topics/components/TopicsSection";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { PERMISSIONS } from "../../../constants/permissions";

interface AlertDetailProps {
  survey: SurveyAlertDto;
  onGoBackClick: (isUpdated: boolean) => void;
}

export default function AlertSurveyDetail({ survey, onGoBackClick }: AlertDetailProps) {
	const navigate = useNavigate();
	const { t } = useTranslation(["survey"]);
	const queryClient = useQueryClient();

	const [editing, setEditing] = useState(false);
	const [isUpdated, setIsUpdated] = useState(false);
	const [loading, setLoading] = useState(false);
	const [topics, setTopics] = useState(survey.topics);
	const [updateAlertSurvey, setUpdateAlertSurvey] = useState(survey);

	const { data: users } = useUsersSelect({});
	const { data: currentClient } = useCurrentClient();
	const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: true });
	
    const currentUserPermissions = useSelector((state: RootState) => state.currentUserInfo.data.permissions);

	const handleSave = async () => {
		setLoading(true);
		const payload = { ...updateAlertSurvey, topics };
		try {
			await changeIAValuesAlert(payload.idSurvey, payload);
			await queryClient.invalidateQueries({
				queryKey: ["alertSurvey", payload.idSurvey],
			});
			setEditing(false);
			setIsUpdated(true);
			showAlert({
				message: t("survey:detail.modalShowDetailAlertUpdateSuccesfully"),
				type: "success",
				duration: 8000,
			});
		} catch (error) {
			const apiError = error as ApiErrorResponse;
			const apiErrorCode = (apiError as any)?.response?.data?.code ?? apiError?.code;
			const apiErrorMessage = (apiError as any)?.response?.data?.message ?? apiError?.message;
			if (apiErrorCode === "ERROR_SAVE_SURVEY_ALERT_STATE") {
				showAlert({
				message: apiErrorMessage,
				type: "warning",
				duration: 8000,
				});
				return;
			}

			showAlert({
				message: t("survey:detail.modalShowDetailSurveyUpdateError"),
				type: "danger",
				duration: 8000,
			});
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		setEditing(false);
		onGoBackClick(isUpdated);
	};

	return (
		<AlertDetailWrapper
			loading={loading}
			editing={editing}
			isVisible={isVisible}
			alert={alert}
			onHideAlert={hideAlert}
			onEditClick={() => setEditing(true)}
			onBackClick={handleBack}
			onSaveClick={handleSave}
		>
			<Row>
				<Col xs={6} md={6}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyIdSurvey")}</Form.Label>
					<Form.Control type="text" value={updateAlertSurvey.idSurvey} disabled />
				</Form.Group>
				</Col>
				<Col xs={6} md={6}>
				<Form.Label>{t("survey:detail.modalShowDetailSurveyUserRelatedSelect")}</Form.Label>
				<Form.Select
					className="form-control"
					value={updateAlertSurvey.idUserRelated ?? ""}
					disabled={!editing}
					onChange={(e) =>
					setUpdateAlertSurvey({
						...updateAlertSurvey,
						idUserRelated: e.target.value,
					})
					}
				>
					<option value="">{t("survey:detail.noUserAssigned")}</option>
					{users?.map((user) => (
					<option key={user.id} value={user.id}>
						{user.name}
					</option>
					))}
				</Form.Select>
				</Col>
			</Row>

			<Row>
				<Col xs={6} md={6}>
				<Form.Group>
					<Form.Label>NPS</Form.Label>
					<Form.Control type="text" value={updateAlertSurvey.nps ?? ""} disabled />
				</Form.Group>
				</Col>
				<Col xs={6} md={6}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyDateSurvey")}</Form.Label>
					<Form.Control
						type="text"
						value={convertUnixToFormateDate(updateAlertSurvey.createDate) ?? ""}
						disabled
					/>
				</Form.Group>
				</Col>
			</Row>

			<Row>
				<Col xs={6} md={6}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyAISentiment")}</Form.Label>
					<SentimentIndicator
						value={updateAlertSurvey.feelingAI ?? null}
						disabled={true}
						showSentimentLabel={false}
						minValue={currentClient?.sentimentMinimumValue}
						maxValue={currentClient?.sentimentMaximumValue}
						onChange={(newValue) => {
							if (newValue == null) return;
							setUpdateAlertSurvey({ ...updateAlertSurvey, feelingAI: newValue });
						}}
					/>
					{updateAlertSurvey.originalFeelingAI != null &&
					updateAlertSurvey.feelingAI !== updateAlertSurvey.originalFeelingAI && (
						<p style={{ color: "green", marginTop: "10px" }}>
							{t("survey:detail.modalShowDetailSurveyAISentimentGenerated")}{" "}
							{updateAlertSurvey.originalFeelingAI}
						</p>
					)}
				</Form.Group>
				</Col>
				<Col xs={6} md={6}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyDateResponse")}</Form.Label>
					<Form.Control
						type="text"
						value={convertUnixToFormateDate(updateAlertSurvey.answerDate) ?? ""}
						disabled
					/>
				</Form.Group>
				</Col>
			</Row>

			<Row>
				<Col xs={6} md={6}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyAIAlert")}</Form.Label>
					<GenerateAlertSelector
						value={updateAlertSurvey.alertAI ?? null}
						onChange={(newValue) => {
							if (newValue == null) return;
							setUpdateAlertSurvey({ ...updateAlertSurvey, alertAI: newValue });
						}}
						disabled={true}
						minValue={currentClient?.sentimentMinimumValue}
						maxValue={currentClient?.sentimentMaximumValue}
						neutralReference={currentClient?.neutralReference}
						alertMinimumValue={currentClient?.alertMinimumValue}
						alertMaximumValue={currentClient?.alertMaximumValue}
					/>
					{updateAlertSurvey.originalAlertAI != null &&
					updateAlertSurvey.alertAI !== updateAlertSurvey.originalAlertAI && (
						<p style={{ color: "green", marginTop: "10px" }}>
							{t("survey:detail.modalShowDetailSurveyAIAlertGenerated")}{" "}
							{updateAlertSurvey.originalAlertAI}
						</p>
					)}
				</Form.Group>
				</Col>
			</Row>

			<Row>
				<Col xs={12} md={12}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyAIMitssage")}</Form.Label>
					<Form.Control
						as="textarea"
						rows={5}
						value={updateAlertSurvey.comment ?? ""}
						disabled
					/>
				</Form.Group>
				</Col>
			</Row>

			<TopicsSection
				topics={topics}
				setTopics={setTopics}
				editing={false}
				newTopicLabel={t("survey:detail.modalShowDetailButtonNewTopic")}
			/>

			<Row>
				<Col xs={12} md={12}>
				<Form.Group>
					<div className="mt-3">
						<Form.Label>{t("survey:detail.modalShowDetailSurveyAIRecommendation")}</Form.Label>
						{currentUserPermissions.length > 0 && (currentUserPermissions.includes(PERMISSIONS.SURVEYS_PATIENTS.VIEW) || currentUserPermissions.includes(PERMISSIONS.SURVEYS_PATIENTS.EDIT)) && (
							<Button variant="secondary" className="float-end" onClick={() => navigate(`/survey-patient/historic?survey=${updateAlertSurvey.idSurvey}&from=alert`)}>{t("survey:detail.viewSurveyData")}</Button>
						)}
					</div>
					<Form.Control
						as="textarea"
						rows={5}
						value={updateAlertSurvey.recommendationAI ?? ""}
						disabled
						onChange={(e) =>
							setUpdateAlertSurvey({
								...updateAlertSurvey,
								recommendationAI: e.target.value,
							})
						}
					/>
				</Form.Group>
				</Col>
			</Row>

			<Row>
				<Col xs={12} md={12}>
				<Form.Group>
					<Form.Label>{t("survey:detail.modalShowDetailSurveyAIAnswer")}</Form.Label>
					<Form.Control
						as="textarea"
						rows={5}
						value={updateAlertSurvey.messageAI ?? ""}
						disabled
						onChange={(e) =>
							setUpdateAlertSurvey({
								...updateAlertSurvey,
								messageAI: e.target.value,
							})
						}
					/>
				</Form.Group>
				</Col>
			</Row>
		</AlertDetailWrapper>
	);
}
